import React, { Fragment, useEffect, useState } from 'react';
import {
    IndexTable,
    useIndexResourceState,
    Layout,
    Text,
    Card,
    BlockStack,
    Box,
    InlineStack,
    Button,
    ButtonGroup,
    FormLayout,
    TextField,
    Select
} from '@shopify/polaris';
import {
    PlusIcon,
    EditIcon,
    DeleteIcon
} from '@shopify/polaris-icons';
import { useDispatch, useSelector } from 'react-redux';
import { getMetafieldsAsync, deleteMetafieldAsync, updateMetafieldAsync, getSettingAsync } from '../../redux/setting/index';
import { createMetafieldDefinitionAsync } from '../../redux/priceUpdate/index';
import { getAllPriceUpdate } from '../../redux/priceUpdate/slice';
import { getAllSetting } from '../../redux/setting/slice';
import Placeholder from '../common/Placeholder';
import ModalComponent from '../common/Modal';
import { METAFIELDS_HEADINGS, NUMERIC_METAFIELD_TYPES, RESERVED_FORMULA_NAMES, dynamicResourceName, extractDate, toFormulaVariable, SKIPP_ACTIONS } from '../../utils';

const MetafieldMapping = ({ toggleActive }) => {
    const dispatch = useDispatch();
    const { metafieldsData, isMetafieldsLoading, isDeleteMetafieldLoading, isUpdateMetafieldLoading, settingData } = useSelector(getAllSetting);
    const { isMetafieldDefinitionLoading } = useSelector(getAllPriceUpdate);
    const [reCall, setReCall] = useState(false);
    const [isModal, setIsModal] = useState({
        open: false,
        type: 'add',
        id: '',
        metafieldDefinitionId: ''
    });
    const [metafieldObject, setMetafieldObject] = useState({
        name: '',
        description: '',
        namespace: '',
        key: '',
        type: 'single_line_text_field',
        ownerType: 'PRODUCTVARIANT'
    });
    const options = [
        { label: 'Single Line Text Field', value: 'single_line_text_field' },
        { label: 'Number Integer', value: 'number_integer' },
        { label: 'Number Decimal', value: 'number_decimal' },
    ];

    useEffect(() => {
        dispatch(getMetafieldsAsync());
    }, [dispatch, reCall]);

    /* settingData carries can_add_custom_metafield and is otherwise only loaded by the
       General tab, which is not currently shown, so fetch it here too. */
    useEffect(() => {
        dispatch(getSettingAsync());
    }, [dispatch]);

    const handleModal = (open, type, id = '', metafieldDefinitionId = '') => {
        setIsModal({ open, type, id, metafieldDefinitionId });
        if (type === 'edit') {
            const findObj = metafieldsData?.find((item) => item?._id === id);
            setMetafieldObject({
                name: findObj?.name,
                description: findObj?.description,
                namespace: findObj?.namespace,
                key: findObj?.key,
                type: findObj?.type,
                ownerType: findObj?.ownerType
            });
        } else {
            setMetafieldObject({
                name: '',
                description: '',
                namespace: '',
                key: '',
                type: 'single_line_text_field',
                ownerType: 'PRODUCTVARIANT'
            });
        }
    };

    const handleChange = (field, value) => {
        setMetafieldObject({ ...metafieldObject, [field]: value });
    };

    const handleSubmit = () => {
        if (isModal.type === 'add') {
            dispatch(createMetafieldDefinitionAsync({
                payload: [metafieldObject],
                callback: (success, message) => {
                    toggleActive(success, message);
                    setReCall(!reCall);
                    handleModal(false, isModal.type);
                }
            }));
        } else if (isModal.type === 'edit') {
            dispatch(updateMetafieldAsync({
                id: isModal.id,
                payload: metafieldObject,
                callback: (success, message) => {
                    if (success) {
                        toggleActive(success, message);
                        setReCall(!reCall);
                        handleModal(false, isModal.type);
                    }
                }
            }));
        } else if (isModal.type === 'delete') {
            dispatch(deleteMetafieldAsync({
                id: isModal.id,
                payload: {
                    metafieldDefinitionId: isModal.metafieldDefinitionId
                },
                callback: (success, message) => {
                    if (success) {
                        toggleActive(success, message);
                        setReCall(!reCall);
                        handleModal(false, isModal.type);
                    }
                }
            }));
        }
    };

    const { selectedResources, allResourcesSelected, handleSelectionChange } = useIndexResourceState(metafieldsData);

    const rowMarkup = metafieldsData.map(
        (
            { _id, type, name, namespace, key, createdAt, ownerType, metafieldDefinitionId },
            index,
        ) => (
            <IndexTable.Row
                id={_id}
                key={_id}
                selected={selectedResources.includes(_id)}
                position={index}
            >
                <IndexTable.Cell>
                    <Text variant="bodyMd" fontWeight="bold" as="span">
                        {type}
                    </Text>
                </IndexTable.Cell>
                <IndexTable.Cell>{name}</IndexTable.Cell>
                <IndexTable.Cell>{`${namespace}.${key}`}</IndexTable.Cell>
                <IndexTable.Cell>{extractDate(createdAt)}</IndexTable.Cell>
                <IndexTable.Cell>{ownerType}</IndexTable.Cell>
                {!SKIPP_ACTIONS.includes(name) ?
                    <IndexTable.Cell>
                        <ButtonGroup gap='tight'>
                            <Button
                                icon={EditIcon}
                                variant='secondary'
                                tone='success'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleModal(true, 'edit', _id, metafieldDefinitionId);
                                }}
                            ></Button>
                            <Button
                                icon={DeleteIcon}
                                variant='secondary'
                                tone='critical'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleModal(true, 'delete', _id, metafieldDefinitionId)
                                }}
                            ></Button>
                        </ButtonGroup>
                    </IndexTable.Cell> :
                    <IndexTable.Cell>
                        <Text variant='bodySm' tone='subdued'>
                            Not Applicable
                        </Text>
                    </IndexTable.Cell>
                }
            </IndexTable.Row>
        ),
    );

    /* A key that cannot become a formula variable would create a field the merchant
       can fill in but never use, so catch it here instead of letting them find out
       later when their formula is rejected. */
    const keyError = (() => {
        const key = metafieldObject.key?.trim();
        if (!key) return '';
        const variable = toFormulaVariable(key);
        if (RESERVED_FORMULA_NAMES.includes(variable)) {
            return `"${key}" is reserved. Choose another key.`;
        }
        if (!/^[A-Za-z][A-Za-z0-9]*$/.test(variable)) {
            return 'Key must start with a letter and use only letters, numbers, underscores or hyphens.';
        }
        return '';
    })();

    const formulaVariableHint = (() => {
        const key = metafieldObject.key?.trim();
        if (!key || keyError) return 'Unique name within the namespace.';
        if (!NUMERIC_METAFIELD_TYPES.includes(metafieldObject.type)) {
            return 'Unique name within the namespace. Choose a numeric type to use this in a formula.';
        }
        return `Unique name within the namespace. Use ${toFormulaVariable(key)} in your custom formula.`;
    })();

    const hasEmptyField = Object.values(metafieldObject).some(
        (value) => typeof value === 'string' && value.trim() === ''
    );
    const deleteName = metafieldsData.find(({ _id }) => _id === isModal.id)?.name;

    return (
        <Layout.Section>
            <Box padding='200'>
                <InlineStack align='space-between' blockAlign='center'>
                    <BlockStack gap='100'>
                        <Text variant='headingMd'>Metafield Mapping</Text>
                        <Text variant='bodySm' tone='subdued'>
                            Map your metafields to the product
                        </Text>
                    </BlockStack>
                    {settingData?.can_add_custom_metafield &&
                        <Button
                            variant='primary'
                            onClick={() => handleModal(true, 'add')}
                            icon={PlusIcon}
                        >
                            Add Metafield
                        </Button>
                    }
                </InlineStack>
                <Placeholder />
                <Card roundedAbove='sm' padding='0'>
                    <IndexTable
                        resourceName={dynamicResourceName('metafield')}
                        itemCount={metafieldsData.length}
                        selectedItemsCount={
                            allResourcesSelected ? 'All' : selectedResources.length
                        }
                        onSelectionChange={handleSelectionChange}
                        headings={METAFIELDS_HEADINGS}
                        loading={isMetafieldsLoading}
                        selectable={false}
                    >
                        {rowMarkup}
                    </IndexTable>
                </Card>
            </Box>
            <ModalComponent
                title={isModal.type === 'add' ? 'Add Metafield Configuration' : isModal.type === 'edit' ? 'Update Metafield Configuration' : 'Delete Metafield'}
                open={isModal.open}
                handleModalClose={() => handleModal(false, isModal.type)}
                primaryAction={{
                    tone: isModal.type === 'delete' ? 'critical' : '',
                    content: isModal.type === 'add' ? 'Add metafield' : isModal.type === 'delete' ? 'Delete metafield' : 'Update Metafield Configuration',
                    onAction: handleSubmit,
                    disabled: isModal.type !== 'delete' && (hasEmptyField || keyError !== ''),
                    loading: isDeleteMetafieldLoading || isMetafieldDefinitionLoading || isUpdateMetafieldLoading
                }}
            >
                <BlockStack gap="200">
                    {isModal.type === 'add' &&
                        <BlockStack gap="100">
                            <Text variant="bodyMd" as="p">
                                Define custom metafields for your product variants. These fields help you store extra product information like weight, purity, or base price.
                            </Text>
                            <Text variant="bodySm" as="p" tone="caution">
                                <strong>Note:</strong> Use a numeric type to make this usable in a pricing formula. A key such as <strong>diamond_price</strong> becomes <strong>diamondPrice</strong> in your custom formula.
                            </Text>
                        </BlockStack>
                    }
                    {isModal.type === 'delete' &&
                        <BlockStack gap="100">
                            <Text tone='subdued' as="p">
                                Are you sure you want to delete <strong>{deleteName}</strong> metafield? This action cannot be undone. You will not be able to recover the metafield once it is deleted.
                            </Text>
                        </BlockStack>
                    }
                    {isModal.type !== 'delete' &&
                        <Fragment>
                            <FormLayout.Group condensed>
                                <TextField
                                    label="Name"
                                    value={metafieldObject.name}
                                    onChange={(value) => handleChange('name', value)}
                                    helpText="Display label for the metafield."
                                    requiredIndicator
                                />
                                <Select
                                    label="Type"
                                    options={options}
                                    onChange={(value) => handleChange('type', value)}
                                    value={metafieldObject.type}
                                    helpText="Select the type for the metafield."
                                    requiredIndicator
                                    readOnly={isModal?.type === 'edit'}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group condensed>
                                <TextField
                                    label="Namespace"
                                    value={metafieldObject.namespace}
                                    onChange={(value) => handleChange('namespace', value)}
                                    helpText="Group name for metafields."
                                    requiredIndicator
                                    readOnly={isModal?.type === 'edit'}
                                />
                                <TextField
                                    label="Key"
                                    value={metafieldObject.key}
                                    onChange={(value) => handleChange('key', value)}
                                    helpText={formulaVariableHint}
                                    error={keyError}
                                    requiredIndicator
                                    readOnly={isModal?.type === 'edit'}
                                />
                            </FormLayout.Group>
                            <FormLayout.Group condensed>
                                <TextField
                                    label="Description"
                                    value={metafieldObject.description}
                                    onChange={(value) => handleChange('description', value)}
                                    helpText="Brief explanation of the field character limit 100."
                                    multiline={2}
                                />
                            </FormLayout.Group>
                        </Fragment>
                    }
                    {isModal.type === 'edit' && (
                        <Text variant="bodySm" as="p" tone="critical">
                            <strong>Note:</strong> Updating this metafield will temporarily stop automatic price updates until you reconfigure the pricing formula.
                        </Text>
                    )}
                </BlockStack>
            </ModalComponent>
        </Layout.Section>
    );
};

export default MetafieldMapping;